import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@lib/firebase';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogEntry {
  level: LogLevel;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
  url: string;
  userAgent: string;
  bufferedAt?: string;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string | null;
  };
}

export interface CloudLoggingCredentials {
  authHeader?: string;
  [key: string]: unknown;
}

export interface CloudLoggingStatus {
  enabled: boolean;
  hasCredentials: boolean;
  bufferSize: number;
  retryAttempts: number;
  maxRetryAttempts: number;
}

class LoggingService {
  private static cloudLoggingEnabled = false;
  private static cloudLoggingCredentials: CloudLoggingCredentials | null = null;
  private static logBuffer: LogEntry[] = [];
  private static maxBufferSize = 100;
  private static retryAttempts = 0;
  private static maxRetryAttempts = 3;

  /**
   * Create a log entry with level, message, and additional data
   */
  static log(level: LogLevel, message: string, data: Record<string, unknown> = {}): LogEntry {
    const normalizedLevel = (level as string).toUpperCase() as LogLevel;
    const logEntry: LogEntry = {
      level: normalizedLevel,
      message,
      data,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : 'Node.js',
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'N/A'
    };

    // Console logging in development and test environments
    // eslint_disable-next-line no-undef
    const isDevelopment = import.meta.env.MODE !== 'production' &&
      (typeof process === 'undefined' || (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production'));

    if (isDevelopment) {
      const logMethod = level.toLowerCase();
      const consoleMethod = ['debug', 'info', 'warn', 'error'].includes(logMethod) ? logMethod : 'log';
      // eslint_disable-next-line no-console
      (console as any)[consoleMethod](message, data);
    }

    return logEntry;
  }

  static debug(message: string, data: Record<string, unknown> = {}): LogEntry {
    return this.log('DEBUG', message, data);
  }

  static info(message: string, data: Record<string, unknown> = {}): LogEntry {
    return this.log('INFO', message, data);
  }

  static warn(message: string, data: Record<string, unknown> = {}): LogEntry {
    return this.log('WARN', message, data);
  }

  static error(message: string, error: unknown = null, context: Record<string, unknown> = {}): LogEntry {
    let errorData = {};

    if (error instanceof Error) {
      errorData = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: (error as any).code || null
      };
    } else if (typeof error === 'object' && error !== null) {
      errorData = error;
    } else if (typeof error === 'string') {
      errorData = { message: error };
    }

    return this.log('ERROR', message, {
      error: errorData,
      context
    });
  }

  static async logAuditEvent(
    actorId: string,
    action: string,
    targetType: string,
    targetId: string,
    status: string,
    metadata: Record<string, unknown> = {}
  ): Promise<string | null> {
    try {
      const timestamp = new Date().toISOString();
      const RETENTION_DAYS = 1095; // 3 years
      const retentionExpiresAt = new Date(Date.now() + RETENTION_DAYS * 24 * 60 * 60 * 1000).toISOString();

      const auditEntry = {
        userId: actorId,
        action,
        resource: targetType,
        resourceId: targetId,
        status,
        timestamp,
        serverTimestamp: serverTimestamp(),
        metadata,
        retentionExpiresAt,
        ipAddress: 'client-side',
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown'
      };

      if (db) {
        const docRef = await addDoc(collection(db, 'auditLogs'), auditEntry);
        return docRef.id;
      }
      return null;
    } catch (err) {
      console.error('Failed to log audit event:', err);
      return null;
    }
  }

  static async sendToCloudLogging(entry: LogEntry): Promise<any> {
    if (!this.cloudLoggingEnabled) {
      this.bufferLog(entry);
      return Promise.resolve({
        status: 'buffered',
        message: 'Cloud Logging not enabled, log buffered locally'
      });
    }

    try {
      const response = await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.cloudLoggingCredentials?.authHeader && {
            Authorization: this.cloudLoggingCredentials.authHeader
          })
        } as HeadersInit,
        body: JSON.stringify(entry)
      });

      if (!response.ok) {
        throw new Error(`Cloud Logging error: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      this.logError('Cloud Logging failed', error, {
        entry,
        attempt: this.retryAttempts
      });
      this.bufferLog(entry);
      return Promise.resolve({
        status: 'error',
        message: `Cloud Logging failed: ${error instanceof Error ? error.message : String(error)}`,
        buffered: true
      });
    }
  }

  static setCloudLoggingCredentials(credentials: CloudLoggingCredentials): void {
    if (credentials && typeof credentials === 'object') {
      this.cloudLoggingCredentials = credentials;
      this.cloudLoggingEnabled = true;
      this.info('Cloud Logging enabled');
      this.flushLogBuffer();
    }
  }

  static getCloudLoggingStatus(): CloudLoggingStatus {
    return {
      enabled: this.cloudLoggingEnabled,
      hasCredentials: !!this.cloudLoggingCredentials,
      bufferSize: this.logBuffer.length,
      retryAttempts: this.retryAttempts,
      maxRetryAttempts: this.maxRetryAttempts
    };
  }

  static bufferLog(entry: LogEntry): void {
    if (this.logBuffer.length < this.maxBufferSize) {
      this.logBuffer.push({
        ...entry,
        bufferedAt: new Date().toISOString()
      });
    } else {
      this.logBuffer.shift();
      this.logBuffer.push({
        ...entry,
        bufferedAt: new Date().toISOString()
      });
    }
  }

  static getLogBuffer(): LogEntry[] {
    return [...this.logBuffer];
  }

  static clearLogBuffer(): void {
    this.logBuffer = [];
    this.retryAttempts = 0;
  }

  static async retryCloudLogging(): Promise<number> {
    if (!this.cloudLoggingEnabled) {
      return Promise.resolve(0);
    }

    if (this.retryAttempts >= this.maxRetryAttempts) {
      this.warn('Max retry attempts reached for Cloud Logging');
      return Promise.resolve(0);
    }

    this.retryAttempts += 1;
    const buffer = [...this.logBuffer];
    let successCount = 0;

    for (const entry of buffer) {
      try {
        const response = await fetch('/api/logs/batch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.cloudLoggingCredentials?.authHeader && {
              Authorization: this.cloudLoggingCredentials.authHeader
            })
          } as HeadersInit,
          body: JSON.stringify([entry])
        });

        if (response.ok) {
          this.logBuffer = this.logBuffer.filter(e => e !== entry);
          successCount += 1;
        }
      } catch (error) {
        this.error('Retry failed for Cloud Logging entry', error);
      }
    }

    if (this.logBuffer.length === 0) {
      this.retryAttempts = 0;
    }

    return Promise.resolve(successCount);
  }

  static async flushLogBuffer(): Promise<number> {
    if (!this.cloudLoggingEnabled || this.logBuffer.length === 0) {
      return Promise.resolve(0);
    }

    try {
      const buffer = [...this.logBuffer];
      const response = await fetch('/api/logs/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.cloudLoggingCredentials?.authHeader && {
            Authorization: this.cloudLoggingCredentials.authHeader
          })
        } as HeadersInit,
        body: JSON.stringify(buffer)
      });

      if (response.ok) {
        this.clearLogBuffer();
        this.info(`Flushed ${buffer.length} logs to Cloud Logging`);
        return Promise.resolve(buffer.length);
      }

      return Promise.resolve(0);
    } catch (error) {
      this.error('Failed to flush log buffer to Cloud Logging', error);
      return Promise.resolve(0);
    }
  }

  // Internal helper for logging errors about logging
  private static logError(message: string, error: unknown, context: Record<string, unknown> = {}) {
    // We don't want to recursively loop if error logging fails, so we use console directly
    if (process.env.NODE_ENV !== 'production') {
      console.error(message, error, context);
    }
  }
}

export default LoggingService;
